// src/components/FollowButtonMobile.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../shared/api/axios";
import { useAuth } from "../../shared/contexts/AuthContext";
import { useTranslation } from "react-i18next";

type Props = {
  authorId?: string;
  profilename?: string;
  label?: string;
};

/** ---------- tiny in-module cache & dedupe ---------- */
type CacheEntry = { isFollowing: boolean; at: number };
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<boolean>>();

/** ---------- visibility hook ---------- */
function useIsVisible<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => setVisible(e.isIntersecting),
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, visible };
}

const FollowButtonMobile: React.FC<Props> = ({ authorId, profilename, label = "Follow" }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // IMPORTANT: read token live; don’t freeze it in a memo with [] deps
  const [targetId, setTargetId] = useState<string | null>(authorId || null);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [initDone, setInitDone] = useState<boolean>(false);

  const { ref: hostRef, visible } = useIsVisible<HTMLButtonElement>(0.4);

  /** Resolve user id from profilename (if needed) */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (targetId || !profilename) return;
      try {
        const res = await api.get(
          `/follows/by-handle/${encodeURIComponent(profilename)}`
        );
        
        const id = res.data?._id;
        if (!cancelled && id) setTargetId(id);
      } catch (e) {
        // soft-fail: leave targetId null
        // console.warn("Handle->id resolve failed:", e);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [profilename, targetId]);

  /** Load follow status — only if:
   *  - we have an id
   *  - the button is visible
   *  - cache is stale/missing
   *  - and we’re not already inflight for this id
   */
  useEffect(() => {
    let cancelled = false;
    if (!targetId || !isAuthenticated) { setInitDone(true); return; }
    if (!visible) return; // defer until visible

    const now = Date.now();
    const cached = cache.get(targetId);
    if (cached && now - cached.at < CACHE_TTL_MS) {
      setIsFollowing(cached.isFollowing);
      setInitDone(true);
      return;
    }

    if (inflight.has(targetId)) {
      inflight.get(targetId)!.then((val) => {
        if (!cancelled) {
          setIsFollowing(val);
          setInitDone(true);
        }
      });
      return;
    }

    const p = api.get(`/follows/${targetId}/stats`)
      .then((res) => {
        const val = !!res.data?.isFollowing;
        cache.set(targetId, { isFollowing: val, at: Date.now() });
        return val;
      })
      .finally(() => {
        inflight.delete(targetId);
      });

    inflight.set(targetId, p);
    p.then((val) => {
      if (!cancelled) {
        setIsFollowing(val);
        setInitDone(true);
      }
    });

    return () => { cancelled = true; };
  }, [targetId, visible]);

  /** Hide if it's me */
  if ((targetId && user?._id === targetId) || (!targetId && user?.profilename === profilename)) {
    return null;
  }

  const onClick = async () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!targetId) {
      if (profilename) navigate(`/visitor-profile/${encodeURIComponent(profilename)}`);
      return;
    }

    setLoading(true);
    try {
      if (isFollowing) {
        await api.delete(`/follows/${targetId}`);
        setIsFollowing(false);
        cache.set(targetId, { isFollowing: false, at: Date.now() }); // keep cache in sync
      } else {
        await api.post(`/follows/${targetId}`);
        setIsFollowing(true);
        cache.set(targetId, { isFollowing: true, at: Date.now() });
      }
    } catch (e) {
      console.error("Follow toggle failed:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      ref={hostRef}
      onClick={onClick}
      disabled={loading || !initDone}
      aria-pressed={isFollowing}
        title={isFollowing ? t('followButton.unfollow') : t('followButton.follow')}

      style={{
        marginLeft: "auto",
        backgroundColor: "#111",
        color: isFollowing ? "#d4af37" : "#bbb",
        border: isFollowing ? "1px solid #d4af37" : "1px solid #ccc",
        borderRadius: 6,
        padding: "2px 4px",
        fontSize: 12,
        cursor: loading ? "not-allowed" : "pointer",
        transition: "all 0.2s ease",
        opacity: loading || !initDone ? 0.7 : 1,
        minWidth: 92,
      }}
    >
   {isFollowing ? t('followButton.unfollow') : (label || t('followButton.follow'))}
    </button>  
  );
};

export default FollowButtonMobile;
